import type { ModuleLoader, BuilderPublicPackagesUrls, IPlatformLogger } from '@wix/thunderbolt-symbols'
import type { IBuilderComponentSdkLoader } from '../../types'

export const isPilerComponent = (componentUrl: string) => componentUrl.includes('piler-router')

export const createSsrBuilderComponentSdkLoader = (
	moduleLoader: ModuleLoader,
	builderPublicPackagesUrls: BuilderPublicPackagesUrls,
	builderComponentsImportMapSdkUrls: Record<string, string>,
	logger: IPlatformLogger
): IBuilderComponentSdkLoader => {
	const ssrLoader = async (url: string) => {
		let dependenciesMap = {}

		if (isPilerComponent(url)) {
			const builderState = await moduleLoader.loadModule(builderPublicPackagesUrls.umd['builder-state'])

			const componentSdkEntries = Object.entries(builderComponentsImportMapSdkUrls).filter(
				([_, url]) => !url.includes('piler-router')
			)

			const componentSdkLoads = componentSdkEntries.map(([key, url]) =>
				moduleLoader
					.loadModule(
						url,
						{},
						// @ts-expect-error | ssr worker bridge
						(code: string) => globalThis.esmToUmd(code, url)
					)
					.then((module) => [key, module] as const)
			)

			const [builderVelo, ...componentSdkPairs] = await Promise.all([
				moduleLoader.loadModule(builderPublicPackagesUrls.umd['builder-velo'], { '@wix/builder-state': builderState }),
				...componentSdkLoads,
			])

			const loadedComponentSdkMap = Object.fromEntries(componentSdkPairs)

			dependenciesMap = {
				'@wix/builder-state': builderState,
				'@wix/builder-velo': builderVelo,
				...loadedComponentSdkMap,
				// @ts-expect-error | pass node env for isolated renderer flow (thunderbolt-app/src/server/isolated-renderer/global-env.ts)
				...(globalThis.nodeEnv ?? {}),
			}
		}

		return moduleLoader
			.loadModule(
				url,
				dependenciesMap,
				// @ts-expect-error | ssr worker bridge
				(code: string) => globalThis.esmToUmd(code, url)
			)
			.then((sdk: any) => {
				if (typeof sdk === 'function') {
					// UMD module that exports a function directly
					return sdk
				} else {
					// Try to find any function in the exports
					return sdk.default || sdk
				}
			})
			.catch((e) => {
				logger.captureError(new Error('Load SDK in platform SSR failed'), {
					groupErrorsBy: 'values',
					tags: { method: 'createSsrBuilderComponentSdkLoader' },
					extra: { deps: `${(e as Error).message}` },
				})
				throw e
			})
	}

	return {
		loadSdk: ssrLoader,
	}
}
